import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { shopifyFetch, PRODUCTS_QUERY, COLLECTION_BY_HANDLE, nodes } from "@/lib/shopify";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function ProductShelf({
  eyebrow,
  title,
  accent,
  linkTo,
  linkLabel = "View all",
  collectionHandle,
  sortKey,
  reverse,
  query,
  limit = 4,
  exclude,
  preferHandles,
}) {
  const [products, setProducts] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const fetchCount = preferHandles?.length || exclude?.length ? 30 : limit;
        let items = [];
        if (collectionHandle) {
          const d = await shopifyFetch(COLLECTION_BY_HANDLE, {
            handle: collectionHandle,
            first: fetchCount,
          });
          items = d.collection ? nodes(d.collection.products) : [];
        }
        if (!collectionHandle || items.length === 0) {
          const d = await shopifyFetch(PRODUCTS_QUERY, {
            first: fetchCount,
            sortKey: sortKey || "BEST_SELLING",
            reverse: !!reverse,
            query: query || null,
          });
          items = nodes(d.products);
        }
        if (preferHandles?.length) {
          items = [...items].sort((a, b) => {
            const ai = preferHandles.indexOf(a.handle);
            const bi = preferHandles.indexOf(b.handle);
            return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
          });
        }
        if (exclude?.length) {
          items = items.filter((p) => !exclude.some((x) => p.handle.includes(x)));
        }
        if (!cancelled) {
          setProducts(items);
          setState("ready");
        }
      } catch {
        if (!cancelled) setState("error");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [collectionHandle, sortKey, reverse, query, limit]);

  return (
    <section
      data-testid={`shelf-${(eyebrow || title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-void py-16 sm:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl lg:text-4xl">
              {title} {accent && <span className="font-editorial italic text-amber">{accent}</span>}
            </h2>
          </div>
          {linkTo && (
            <Link
              to={linkTo}
              data-testid={`shelf-link-${(eyebrow || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-amber"
            >
              {linkLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          )}
        </Reveal>

        {state === "loading" && (
          <div className="flex items-center gap-3 py-16 text-ivory/40" data-testid="shelf-loading">
            <Loader2 className="h-5 w-5 animate-spin text-amber" />
            <span className="font-mono2 text-xs uppercase tracking-[0.25em]">
              Summoning products…
            </span>
          </div>
        )}

        {state === "error" && (
          <p
            data-testid="shelf-error"
            className="rounded-lg border border-amber/30 bg-amber/5 px-5 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-amber"
          >
            Connecting to the Weird O'Clock Shopify store — check back in a tick.
          </p>
        )}

        {state === "ready" && products.length === 0 && (
          <p className="py-10 font-mono2 text-xs uppercase tracking-[0.2em] text-ivory/40">
            Nothing here yet — new weirdness lands soon.
          </p>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4">
          {products.slice(0, limit).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
